import type { PropsWithChildren } from 'react';
import { cloneElement, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { Portal } from '#src/atoms/layout/Portal/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';

const Root = styled(Vertical).attrs({ gap: 0, center: true })`
  position: relative;
  justify-content: flex-end;
  pointer-events: none;
  height: 0;
`;

const OverflowWrapper = styled(Vertical)`
  position: absolute;
  overflow: clip;
  width: 100%;
  bottom: var(--double-unit);
  left: 50%;
  transform: translateX(-50%);
  padding: 0 var(--double-unit);
`;

const Content = styled(Vertical)`
  position: relative;
  width: 100%;
  border-radius: var(--intermediate-unit) var(--intermediate-unit) 0 0;
  pointer-events: auto;
`;
const PortalReceiver = styled.div`
  a,
  button,
  label {
    /** only interractive elements should be able to receive pointer events */
    pointer-events: auto;
  }
`;

const BottomPortalWrapper = ({ children }: PropsWithChildren) => {
  return (
    <Portal id="above-bottom-bar-portal">
      <Root gap={0}>
        <OverflowWrapper>
          <PortalReceiver id="above-bottom-sheet-portal" />
          <Content gap={2.5} center>
            {children}
          </Content>
        </OverflowWrapper>
      </Root>
    </Portal>
  );
};

/**
 * BottomSheetAbove is a component that renders the children in a portal if the screen is mobile
 * and adds a className to the children if the screen is desktop
 */
const BottomPortalAbove = ({ children }: PropsWithChildren) => {
  const { up: isLaptop } = useScreenSize('laptop');
  const portalReceiver = document.getElementById('above-bottom-sheet-portal');
  if (portalReceiver && !isLaptop) {
    return createPortal(children, portalReceiver);
  }
  return isValidElement<{ className?: string }>(children)
    ? cloneElement(children, { className: 'portalDisabled' })
    : null;
};

/** BottomPortal is a mobile only component, it will render nothing on desktop */
export const BottomPortal = (props: PropsWithChildren) => {
  const { up: isLaptop } = useScreenSize('laptop');
  if (isLaptop) {
    return null;
  }
  return <BottomPortalWrapper {...props} />;
};

BottomPortal.Above = BottomPortalAbove;
